/**
 * ErrorBoundary — catches unhandled React errors and shows a recovery UI.
 *
 * Ported from v1's ErrorBoundary (class component, MUI) to v2 (Tailwind + Lucide).
 *
 * Features:
 *   - React class component (required for componentDidCatch)
 *   - Logs error to localStorage for later analysis
 *   - Shows illustration, error message, and recovery actions
 *   - Support email link pre-filled with error + device info
 *   - Scrollable stack trace with copy-to-clipboard
 *   - "Reload" and "Go to start" buttons
 *
 * NEVER-01: This component NEVER deletes user data.
 * NEVER-09: Shows a clear error state, never blank screen.
 */

import {
  Component,
  type ErrorInfo,
  type ReactNode,
  useCallback,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import RotateCcw from "@/shared/icons/pikaicons/stroke/arrows-chevrons/rotate-left.svg?react"
import Home from "@/shared/icons/pikaicons/stroke/building/home-default.svg?react"
import Copy from "@/shared/icons/totodoicons/stroke/copy.svg?react"
import Check from "@/shared/icons/pikaicons/stroke/general/check-tick-single.svg?react"
import AlertTriangle from "@/shared/icons/pikaicons/stroke/alerts/alert-triangle.svg?react"
import { Button } from "@/app/components/ui/button"
import { logError } from "@/shared/telemetry/errorStorage"
import { addBreadcrumb } from "@/shared/telemetry/breadcrumbs"
import {
  handleIndexedDBError,
  isIndexedDBCrashError,
} from "@/shared/utils/platform/handleIndexedDBError"

// ---------------------------------------------------------------------------
// Error logging to localStorage (matches v1 pattern)
// ---------------------------------------------------------------------------
// Error display (functional component for hooks)
// ---------------------------------------------------------------------------

function ErrorDisplay({ error }: { error: Error }) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const errorString = [
    error.message,
    "",
    error.stack ?? "",
    "",
    `URL: ${window.location.href}`,
    `User-Agent: ${navigator.userAgent}`,
    `Timestamp: ${new Date().toISOString()}`,
  ].join("\n")

  const mailSubject = encodeURIComponent(`Bug: ${error.message.slice(0, 100)}`)
  const mailBody = encodeURIComponent(errorString)

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(errorString).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }, [errorString])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-5">
      <div className="w-full max-w-md text-center">
        {/* Error illustration / icon */}
        <div className="mb-6 flex justify-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-10 w-10 text-destructive" />
          </div>
        </div>

        {/* Title */}
        <h1 className="mb-3 text-2xl font-bold">
          {t("Oops, something went wrong")}
        </h1>

        {/* Description */}
        <p className="mb-6 text-sm text-muted-foreground leading-relaxed">
          {t(
            "If none of the below works, please restart the app and try again or contact",
          )}{" "}
          <a
            href={`mailto:support@totodo.app?subject=${mailSubject}&body=${mailBody}`}
            className="font-medium text-primary underline"
          >
            support@totodo.app
          </a>
          .
        </p>

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <Button
            variant="primary"
            size="default"
            onClick={() => window.location.reload()}
          >
            <RotateCcw className="size-4 mr-2" />
            {t("Reload")}
          </Button>
          <Button
            variant="secondary"
            size="default"
            onClick={() => {
              window.location.href = "/"
            }}
          >
            <Home className="size-4 mr-2" />
            {t("Go to start")}
          </Button>
        </div>

        {/* Stack trace */}
        <div className="text-left">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold">{t("Error details")}</h2>
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              {copied ? (
                <>
                  <Check className="size-3" />
                  {t("Copied")}
                </>
              ) : (
                <>
                  <Copy className="size-3" />
                  {t("Copy")}
                </>
              )}
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto overflow-x-hidden rounded-xl bg-muted/50 p-4 text-xs font-mono text-muted-foreground leading-relaxed">
            <div className="text-destructive font-bold mb-2">{error.message}</div>
            {error.stack ?? error.message}
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ErrorBoundary class component
// ---------------------------------------------------------------------------

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: undefined }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // If this is a recoverable IDB crash, handleIndexedDBError (called in
    // componentDidCatch) will reload the page. Skip showing the error screen
    // so the user doesn't see a flash before the reload.
    if (isIndexedDBCrashError(error.message)) {
      return { hasError: false, error }
    }
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to centralized error storage (includes breadcrumbs + App Insights)
    logError(error, {
      source: "boundary",
      severity: "critical",
      componentStack: errorInfo.componentStack,
    })

    addBreadcrumb({
      category: "error",
      message: `ErrorBoundary caught: ${error.message}`,
      level: "error",
    })

    // Console log for development
    console.error("[ErrorBoundary] Caught error:", error, errorInfo)

    // Track error in Firebase Analytics (lazy import to avoid load-order issues)
    import("@/shared/services/analyticsTrackers").then(
      ({ trackErrorOccurred }) => trackErrorOccurred(error.name, error.message),
    ).catch(() => {})

    // Safari/WebKit IDB crash → auto-reload instead of showing error screen.
    // Returns true if reload was initiated (immediate or delayed).
    // If true, suppress error screen — page will reload shortly.
    const reloadInitiated = handleIndexedDBError(error)

    // If we reach here for an IDB crash error and reload was NOT initiated
    // (background tab), show the error screen.
    if (isIndexedDBCrashError(error.message) && !reloadInitiated && !this.state.hasError) {
      this.setState({ hasError: true, error })
    }
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return <ErrorDisplay error={this.state.error} />
    }

    return this.props.children
  }
}
